'use client';

/**
 * LiveLevelCue — the quiet green "Sara is listening" cue (Turn-12 design,
 * 12c): three level bars + label, shown only while the user is actually
 * being heard.
 *
 * Two channels, both driven by the shared audio level engine
 * (useAudioVisualization) with no React state on the audio clock:
 *
 * - BAR HEIGHT follows the LIVE user level: a small rAF reads
 *   `levelRef.current.user` each frame and writes a floored `--mic-level`
 *   CSS var on the container (same pattern + floor as equalizer-bars'
 *   `--eq-level`, so bars never collapse to slivers between words); each
 *   bar's height is `max-height × var`. The design's lvlA/B/C loops ride
 *   on top as bottom-origin scaleY keyframes — transform × height compose,
 *   so the loop can't fight the level response.
 *
 * - VISIBILITY comes from the engine's calibrated activity detection via
 *   the `active` prop (call-screen passes `vizMode === VizMode.USER`) —
 *   not a re-derived ad-hoc threshold here. The old local threshold
 *   (level > 0.05 ≈ raw room noise after the engine's 5× gain) plus a
 *   700ms hangover made the cue read as an always-on loop that ignored
 *   voice.
 *
 * Turn 13 (13d mobile parity) adds optional `barMaxHeights`/`barWidth`
 * overrides so call-top-bar-status.tsx can render the SAME level-driven
 * bars at its 2.5px/15px mini scale, reusing this component's rAF (rather
 * than a second copy of the same `.user` level loop) instead of a
 * hand-rolled duplicate.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { AudioLevels } from '@/hooks/useAudioVisualization';

/** Floor fraction of each bar's max height that always stays visible while
 *  the cue shows — same rationale (and value) as equalizer-bars'
 *  LEVEL_FLOOR: the engine's level genuinely dips near 0 between phonemes,
 *  and bars collapsing to hairlines mid-word reads as flicker. */
const LEVEL_FLOOR = 0.5;

/** Design bar geometry (12c): 3px-wide green bars. Heights are each bar's
 *  MAX from the design's lvlA/B/C pairs (16/14/17) — the scaleY keyframes
 *  (tailwind `mic-lvl-*`) swing each bar between its design min/max as a
 *  fraction of this, and `--mic-level` scales the whole glyph. */
const BAR_MAX_HEIGHTS = [16, 14, 17] as const;
const BAR_ANIMATIONS = [
  'motion-safe:animate-mic-lvl-a',
  'motion-safe:animate-mic-lvl-b',
  'motion-safe:animate-mic-lvl-c',
] as const;

export const LEVEL_CUE_VARIANT = {
  /** Bare bars + label — the desktop status-line treatment. */
  INLINE: 'inline',
  /** Bordered green pill — the compact treatment (mobile controls area). */
  PILL: 'pill',
} as const;
export type LevelCueVariant =
  (typeof LEVEL_CUE_VARIANT)[keyof typeof LEVEL_CUE_VARIANT];

export interface LiveLevelCueProps {
  /** Shared audio level engine ref — reads `.user` each frame. */
  levelRef: React.RefObject<AudioLevels>;
  /** Whether the user is currently being heard — pass the engine's
   *  calibrated verdict (`vizMode === VizMode.USER`), which already has
   *  gain-aware thresholds and a 300ms activity timeout. */
  active: boolean;
  variant?: LevelCueVariant;
  /** Rendered in the same grid cell and cross-faded 1:1 against the cue
   *  (desktop passes the "Listening…" status text). Omit to have the cue
   *  simply fade in/out over empty space. */
  idle?: ReactNode;
  label?: string;
  className?: string;
  /** Override the default 16/14/17px bar geometry — Turn 13's mobile top
   *  bar (13d) needs an exact 2.5px/15px mini scale (call-top-bar-status.tsx).
   *  Defaults preserve the existing INLINE/PILL look for both consumers that
   *  predate Turn 13. */
  barMaxHeights?: readonly number[];
  barWidth?: number;
}

export function LiveLevelCue({
  levelRef,
  active,
  variant = LEVEL_CUE_VARIANT.INLINE,
  idle,
  label = 'Sara is listening',
  className,
  barMaxHeights = BAR_MAX_HEIGHTS,
  barWidth = 3,
}: LiveLevelCueProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const level = Math.max(0, Math.min(1, levelRef.current?.user ?? 0));
      rootRef.current?.style.setProperty(
        '--mic-level',
        (LEVEL_FLOOR + (1 - LEVEL_FLOOR) * level).toFixed(3)
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [levelRef]);

  const isPill = variant === LEVEL_CUE_VARIANT.PILL;

  // Fade timing: instant-in on activity, but the fade-OUT (and the idle
  // slot's matching fade-in) waits 400ms — the engine's activity timeout is
  // 300ms, so a brief inter-phrase dip that flips vizMode for a beat gets
  // absorbed instead of strobing the cue.
  const cueVisibility = active ? 'opacity-[1]' : 'opacity-[0] delay-[400ms]';
  const idleVisibility = active ? 'opacity-[0]' : 'opacity-[1] delay-[400ms]';

  return (
    <div ref={rootRef} className={cn('grid place-items-center', className)}>
      {idle && (
        <div
          className={cn(
            'col-start-1 row-start-1 transition-opacity duration-200',
            idleVisibility
          )}
        >
          {idle}
        </div>
      )}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center transition-opacity duration-200',
          cueVisibility,
          isPill
            ? 'h-[34px] gap-[8px] rounded-[20px] border-[1px] border-[#CFE6D8] bg-[#E8F3EC] px-[13px]'
            : 'gap-[8px]'
        )}
      >
        <div
          className="flex items-end gap-[3px]"
          style={{ height: Math.max(...barMaxHeights) }}
          aria-hidden="true"
        >
          {barMaxHeights.map((maxHeight, i) => (
            <span
              key={i}
              className={cn(
                'origin-bottom rounded-[2px] bg-[#1F8A5B]',
                BAR_ANIMATIONS[i]
              )}
              style={{
                width: barWidth,
                height: `calc(${maxHeight}px * var(--mic-level, 1))`,
              }}
            />
          ))}
        </div>
        <span
          className={cn(
            'font-semibold text-[#1F8A5B]',
            isPill ? 'text-[12px]' : 'text-[13.5px]'
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
